// rtlsFilter.js - RTLS function filter popup

import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import "./rtlsMap.css";

// ─── Timing constants ─────────────────────────────────────────────────────────
// CLOSE_ANIM_MS must match the rtlsf-drop-out duration in rtlsMap.css.
const CLOSE_ANIM_MS = 180;

// ─── Date helpers ─────────────────────────────────────────────────────────────

function toDatetimeLocal(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function fromDatetimeLocal(str) {
  if (!str) return null;
  return Math.floor(new Date(str).getTime() / 1000);
}

function defaultBegin() {
  return toDatetimeLocal(new Date(Date.now() - 8 * 3600 * 1000));
}
function defaultEnd() {
  return toDatetimeLocal(new Date());
}

// ─── Summary builder ──────────────────────────────────────────────────────────
// Produces the single-line text shown in the minimized summary bar.

function formatDateShort(dtLocalStr) {
  if (!dtLocalStr) return "-";
  const d = new Date(dtLocalStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildSummary({
  activeFunction,
  beginDt,
  endDt,
  datetimeDt,
  playbackEndDt,
  labelTags,
  objectTags,
  selectedObject,
  selectedJob,
  mode,
}) {
  if (activeFunction === "playback") {
    return `${formatDateShort(datetimeDt)} → ${formatDateShort(playbackEndDt)}`;
  }
  const parts = [`${formatDateShort(beginDt)} → ${formatDateShort(endDt)}`];
  const objs =
    mode === "dateOnly" ? (selectedObject ? [selectedObject] : []) : objectTags;
  if (objs.length > 0) parts.push(objs.join(", "));
  if (activeFunction === "workflow") {
    if (selectedJob) parts.push(selectedJob.jobName);
  } else if (mode === "full" && labelTags.length > 0) {
    parts.push(labelTags.join(", "));
  }
  return parts.join(" · ");
}

// ─── Static data ──────────────────────────────────────────────────────────────

const FUNCTION_TITLES = {
  heatmap: "Heatmap Options",
  spaghetti: "Spaghetti Map Options",
  playback: "Playback Options",
  workflow: "Workflow Map Options",
};

// ─── Workflow job list (static) ────────────────────────────────────────────
// Each entry pairs a personnel-side job description with a module-side
// operation number so a single dropdown selection can drive BOTH lookup
// paths on the backend /workflow-map route:
//   - Personnel object (e.g. "Antonio Edwards")
//       → RTLS_PersonnelMetricsActivityHist.Description = jobName
//   - Module object (object name starts with "12WO", e.g. "12WO010404")
//       → RTLS_ModuleMetricsActivityHistory.OperationNumber = jobNumber
//
// TODO(business): replace with the authoritative job/operation list. Values
// below are placeholders illustrating the shape, drawn from the sample rows
// in RTLS_PersonnelMetricsActivityHist.txt / RTLS_ModuleMetricsActivityHistory.txt.
const WORKFLOW_JOBS = [
  { isBothTypeJob: false, jobName: "Prefab/Kitting", jobNumber: 20 },
  { isBothTypeJob: true, jobName: "Material Handling", jobNumber: 25 },
  { isBothTypeJob: true, jobName: "Care Package Prep", jobNumber: 27 },
  { isBothTypeJob: true, jobName: "CNC Gypsum", jobNumber: 40 },
  { isBothTypeJob: true, jobName: "Sub-FLooring Install", jobNumber: 100 },
  {
    isBothTypeJob: true,
    jobName: "Ceiling Furring Channel Install",
    jobNumber: 110,
  },
  {
    isBothTypeJob: true,
    jobName: "Roof/Ceiling Waterproofing",
    jobNumber: 120,
  },
  { isBothTypeJob: true, jobName: "Exterior Wall Install", jobNumber: 130 },
  { isBothTypeJob: true, jobName: "Interior Wall install", jobNumber: 135 },
  {
    isBothTypeJob: true,
    jobName: "Bulkhead/Drop Ceiling  Install",
    jobNumber: 137,
  },
  { isBothTypeJob: true, jobName: "Electrical Rough-In", jobNumber: 140 },
  { isBothTypeJob: true, jobName: "Plumbing Rough-In", jobNumber: 150 },
  { isBothTypeJob: true, jobName: "HVAC Rough-In", jobNumber: 160 },
  {
    isBothTypeJob: true,
    jobName: "Fire Protection / Sprinkler",
    jobNumber: 170,
  },
  {
    isBothTypeJob: true,
    jobName: "Gypsum and Install Boundary/Upper Ceiling",
    jobNumber: 180,
  },
  { isBothTypeJob: true, jobName: "Tape and Mud", jobNumber: 185 },
  {
    isBothTypeJob: true,
    jobName: "Floor Underlayment Install",
    jobNumber: 190,
  },
  { isBothTypeJob: true, jobName: "Finish Flooring Install", jobNumber: 195 },
  { isBothTypeJob: true, jobName: "Insulation", jobNumber: 200 },
  { isBothTypeJob: true, jobName: "Fire Caulking", jobNumber: 205 },
  { isBothTypeJob: true, jobName: "Sheathing (Exterior)", jobNumber: 210 },
  { isBothTypeJob: true, jobName: "Finished Exterior", jobNumber: 215 },
  {
    isBothTypeJob: true,
    jobName: "Window and Door Installation",
    jobNumber: 220,
  },
  {
    isBothTypeJob: true,
    jobName: "Roofing",
    jobNumber: 230,
  },
  { isBothTypeJob: true, jobName: "Prime and Paint", jobNumber: 240 },
  {
    isBothTypeJob: true,
    jobName: "Cabinets/Vanities/Countertops Install",
    jobNumber: 250,
  },
  { isBothTypeJob: true, jobName: "Final HVAC", jobNumber: 260 },
  { isBothTypeJob: true, jobName: "Final Electrical", jobNumber: 270 },
  { isBothTypeJob: true, jobName: "Final Plumbing", jobNumber: 280 },
  {
    isBothTypeJob: true,
    jobName: "Final Finish, Appliance Install",
    jobNumber: 300,
  },
  {
    isBothTypeJob: true,
    jobName: "Final Clean Up For Shipment",
    jobNumber: 305,
  },
  { isBothTypeJob: true, jobName: "Final Inspection", jobNumber: 310 },
  { isBothTypeJob: true, jobName: "Close up and Shrink Wrap", jobNumber: 320 },
  {
    isBothTypeJob: true,
    jobName: "3D Finish Material Handling",
    jobNumber: 870,
  },
  { isBothTypeJob: true, jobName: "3D Finish Welding", jobNumber: 880 },
  { isBothTypeJob: true, jobName: "QA Inspection", jobNumber: 9999 },
  { isBothTypeJob: false, jobName: "CLEANING", jobNumber: 27572 },
  { isBothTypeJob: false, jobName: "HOTSHOT", jobNumber: 27574 },
  { isBothTypeJob: false, jobName: "LIGHT-DUTY", jobNumber: 27575 },
  { isBothTypeJob: false, jobName: "MAINT", jobNumber: 27576 },
  { isBothTypeJob: false, jobName: "MEETING", jobNumber: 27577 },
  { isBothTypeJob: false, jobName: "MOVE-MOD", jobNumber: 27578 },
  { isBothTypeJob: false, jobName: "QA-CHECK", jobNumber: 27580 },
  { isBothTypeJob: false, jobName: "SHOP-PROJ", jobNumber: 27581 },
  { isBothTypeJob: false, jobName: "SITE-COMM", jobNumber: 27573 },
  { isBothTypeJob: false, jobName: "SITE-WELD", jobNumber: 27587 },
  { isBothTypeJob: false, jobName: "STARTUP", jobNumber: 27582 },
  { isBothTypeJob: false, jobName: "SUPV-TASKS", jobNumber: 27583 },
  { isBothTypeJob: false, jobName: "TRAINING", jobNumber: 27584 },
  { isBothTypeJob: false, jobName: "WAREHOUSE", jobNumber: 27586 },
];

// ─── Live API fetchers ────────────────────────────────────────────────────────
// window.Riot.Api is guaranteed to be authenticated before RtlsFilter mounts -
// rtlsMap.js only renders <RtlsFilter> after full auth + connected status.

async function fetchLabelNames() {
  try {
    const Api = window.Riot?.Api;
    if (!Api?.labelApi) return [];
    const labels = await Api.labelApi.getLabels();
    if (!Array.isArray(labels)) return [];
    return labels
      .map((l) => (l.name ?? "").trim())
      .filter((n) => n.length > 0)
      .sort((a, b) => a.localeCompare(b));
  } catch (err) {
    console.warn("[RtlsFilter] fetchLabelNames failed:", err);
    return [];
  }
}

async function fetchObjectNames() {
  try {
    const Api = window.Riot?.Api;
    if (!Api?.objectApi) return [];
    const objects = await Api.objectApi.getObjects();
    if (!Array.isArray(objects)) return [];
    return objects
      .map((o) => (o.name ?? "").trim())
      .filter((n) => n.length > 0)
      .sort((a, b) => a.localeCompare(b));
  } catch (err) {
    console.warn("[RtlsFilter] fetchObjectNames failed:", err);
    return [];
  }
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function FunctionIcon({ fn }) {
  switch (fn) {
    case "heatmap":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="7" opacity="0.5" />
          <circle cx="12" cy="12" r="10" opacity="0.25" />
        </svg>
      );
    case "spaghetti":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
          <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
        </svg>
      );
    case "playback":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      );
    case "workflow":
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="5" height="5" rx="1" />
          <rect x="16" y="3" width="5" height="5" rx="1" />
          <rect x="3" y="16" width="5" height="5" rx="1" />
          <rect x="16" y="16" width="5" height="5" rx="1" />
          <line x1="8" y1="5.5" x2="16" y2="5.5" />
          <line x1="5.5" y1="8" x2="5.5" y2="16" />
          <line x1="18.5" y1="8" x2="18.5" y2="16" />
          <line x1="8" y1="18.5" x2="16" y2="18.5" />
        </svg>
      );
    default:
      return null;
  }
}

// Chevron used by the minimize toggle button.
// Points DOWN when expanded (default) → rotates UP when minimized.
function ChevronIcon({ up }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className={`rtlsf-chevron${up ? " rtlsf-chevron--up" : ""}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── HighlightMatch ───────────────────────────────────────────────────────────

function HighlightMatch({ text, query }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <strong className="rtlsf-dropdown-match">
        {text.slice(idx, idx + q.length)}
      </strong>
      {text.slice(idx + q.length)}
    </>
  );
}

// ─── TagSelect ────────────────────────────────────────────────────────────────
//
// Props
//   id               - wires <label htmlFor>
//   placeholder      - shown when no tags and input is empty
//   suggestions      - string[] live from API
//   suggestionsLoading - bool
//   tags             - string[] controlled
//   onChange         - (tags: string[]) => void
//
// Dropdown close rules
//   CLOSES on  : outside mousedown | ESC while dropdown open
//   STAYS OPEN : after tag added | on focus | on typing
//   NEVER on   : blur alone (mousedown on items calls preventDefault
//                which keeps input focus, preventing blur)

const TagSelect = ({
  id,
  placeholder,
  suggestions = [],
  suggestionsLoading = false,
  tags,
  onChange,
  maxTags = Infinity, // workflow's object field passes 1 → new pick replaces old
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filtered list: substring match, already-selected excluded
  const filtered = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    return suggestions.filter(
      (s) => (!q || s.toLowerCase().includes(q)) && !tags.includes(s),
    );
  }, [inputValue, suggestions, tags]);

  // Outside-click → close dropdown
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIdx(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset highlight when list shape changes
  useEffect(() => {
    setHighlightedIdx(-1);
  }, [filtered.length]);

  // addTag - intentionally does NOT close the dropdown.
  // When maxTags is reached (e.g. workflow's single-object field), picking a
  // new value REPLACES the existing tag instead of being ignored/appended.
  const addTag = useCallback(
    (value) => {
      const v = value.trim();
      if (!v || tags.includes(v)) return;
      if (tags.length >= maxTags) {
        onChange([v]);
      } else {
        onChange([...tags, v]);
      }
      setInputValue("");
      setHighlightedIdx(-1);
      inputRef.current?.focus();
    },
    [tags, onChange, maxTags],
  );

  const removeTag = useCallback(
    (tag) => onChange(tags.filter((t) => t !== tag)),
    [tags, onChange],
  );

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (highlightedIdx >= 0 && highlightedIdx < filtered.length) {
          addTag(filtered[highlightedIdx]);
        } else if (inputValue.trim()) {
          addTag(inputValue);
        }
        break;
      case "Backspace":
        if (inputValue === "" && tags.length > 0)
          removeTag(tags[tags.length - 1]);
        break;
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIdx((p) =>
          filtered.length === 0 ? -1 : p < filtered.length - 1 ? p + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIdx((p) =>
          filtered.length === 0 ? -1 : p > 0 ? p - 1 : filtered.length - 1,
        );
        break;
      case "Escape":
        if (isOpen) {
          e.stopPropagation(); // don't let ESC bubble to panel handler
          setIsOpen(false);
          setHighlightedIdx(-1);
        }
        break;
      default:
        break;
    }
  };

  const showDropdown = isOpen && (filtered.length > 0 || suggestionsLoading);

  return (
    <div className="rtlsf-tagselect" ref={containerRef}>
      {/* ── Chip input box ─────────────────────────────────────────────── */}
      <div
        className={`rtlsf-tagselect-box${isOpen ? " rtlsf-tagselect-box--open" : ""}`}
        onClick={() => {
          inputRef.current?.focus();
          setIsOpen(true);
        }}
      >
        {tags.map((tag) => (
          <span key={tag} className="rtlsf-tag">
            <span className="rtlsf-tag-text">{tag}</span>
            <button
              type="button"
              className="rtlsf-tag-remove"
              onMouseDown={(e) => {
                e.preventDefault();
                removeTag(tag);
              }}
              aria-label={`Remove ${tag}`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id={id}
          type="text"
          className="rtlsf-tagselect-input"
          placeholder={tags.length === 0 ? placeholder : ""}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* ── Suggestion dropdown ─────────────────────────────────────────── */}
      {showDropdown && (
        <ul className="rtlsf-dropdown" role="listbox">
          {suggestionsLoading && filtered.length === 0 ? (
            <li className="rtlsf-dropdown-loading">
              <span className="rtlsf-dropdown-loading-spinner" />
              Loading…
            </li>
          ) : (
            filtered.map((item, idx) => (
              <li
                key={item}
                role="option"
                aria-selected={idx === highlightedIdx}
                className={`rtlsf-dropdown-item${
                  idx === highlightedIdx ? " rtlsf-dropdown-item--active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(item);
                }}
                onMouseEnter={() => setHighlightedIdx(idx)}
              >
                <HighlightMatch text={item} query={inputValue} />
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

// ─── JobSelect ────────────────────────────────────────────────────────────────
//
// Single-select searchable dropdown for the Workflow function's static job
// list (WORKFLOW_JOBS). Mirrors TagSelect's interaction model (type to
// filter, arrow keys, Enter to pick, outside-click/ESC to close) but only
// ever holds ONE value, since a workflow run is scoped to exactly one
// object executing exactly one job.
//
// Props
//   id          - wires <label htmlFor>
//   placeholder - shown when nothing is selected and input is empty
//   jobs        - [{ jobName, jobNumber }] static list
//   value       - { jobName, jobNumber } | null   controlled
//   onChange    - (job: { jobName, jobNumber } | null) => void

const JobSelect = ({ id, placeholder, jobs = [], value, onChange }) => {
  const [inputValue, setInputValue] = useState(value ? value.jobName : "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Keep the input text in sync when the value changes externally
  // (e.g. cleared by the parent's "reset on function change" effect).
  useEffect(() => {
    setInputValue(value ? value.jobName : "");
  }, [value]);

  const filtered = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) => j.jobName.toLowerCase().includes(q));
  }, [inputValue, jobs]);

  // Outside-click → close dropdown and revert any unsubmitted typed text
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIdx(-1);
        setInputValue(value ? value.jobName : "");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  useEffect(() => {
    setHighlightedIdx(-1);
  }, [filtered.length]);

  const selectJob = useCallback(
    (job) => {
      onChange(job);
      setInputValue(job.jobName);
      setIsOpen(false);
      setHighlightedIdx(-1);
    },
    [onChange],
  );

  const clearJob = useCallback(() => {
    onChange(null);
    setInputValue("");
    inputRef.current?.focus();
  }, [onChange]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (highlightedIdx >= 0 && highlightedIdx < filtered.length) {
          selectJob(filtered[highlightedIdx]);
        }
        break;
      case "Backspace":
        if (inputValue === "" && value) clearJob();
        break;
      case "ArrowDown":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIdx((p) =>
          filtered.length === 0 ? -1 : p < filtered.length - 1 ? p + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIdx((p) =>
          filtered.length === 0 ? -1 : p > 0 ? p - 1 : filtered.length - 1,
        );
        break;
      case "Escape":
        if (isOpen) {
          e.stopPropagation();
          setIsOpen(false);
          setHighlightedIdx(-1);
          setInputValue(value ? value.jobName : "");
        }
        break;
      default:
        break;
    }
  };

  const showDropdown = isOpen && filtered.length > 0;

  return (
    <div className="rtlsf-tagselect" ref={containerRef}>
      {/* ── Chip input box - reuses TagSelect styling for visual consistency ── */}
      <div
        className={`rtlsf-tagselect-box${isOpen ? " rtlsf-tagselect-box--open" : ""}`}
        onClick={() => {
          inputRef.current?.focus();
          setIsOpen(true);
        }}
      >
        {value && (
          <span className="rtlsf-tag">
            <span className="rtlsf-tag-text">{value.jobName}</span>
            <button
              type="button"
              className="rtlsf-tag-remove"
              onMouseDown={(e) => {
                e.preventDefault();
                clearJob();
              }}
              aria-label={`Remove ${value.jobName}`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </span>
        )}

        {!value && (
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="rtlsf-tagselect-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
        )}
      </div>

      {/* ── Suggestion dropdown ─────────────────────────────────────────── */}
      {showDropdown && (
        <ul className="rtlsf-dropdown" role="listbox">
          {filtered.map((job, idx) => (
            <li
              key={job.jobNumber}
              role="option"
              aria-selected={idx === highlightedIdx}
              className={`rtlsf-dropdown-item${
                idx === highlightedIdx ? " rtlsf-dropdown-item--active" : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectJob(job);
              }}
              onMouseEnter={() => setHighlightedIdx(idx)}
            >
              <HighlightMatch text={job.jobName} query={inputValue} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── RtlsFilter ───────────────────────────────────────────────────────────────

const RtlsFilter = ({
  activeFunction, // "heatmap" | "spaghetti" | "playback" | "workflow"
  mode = "full", // "full" | "dateOnly"
  selectedObject = null, // pre-fills / locks the object name field
  isExpanded = false, // drives compact vs full panel sizing
  isClosingExternal = false, // true when toolbar toggle-off drives the close
  onApply, // (filters) => void
  onClose, // () => void - called after internal close animation
}) => {
  // ── Date / time ─────────────────────────────────────────────────────────────
  const [beginDt, setBeginDt] = useState(defaultBegin);
  const [endDt, setEndDt] = useState(defaultEnd);
  const [datetimeDt, setDatetimeDt] = useState(defaultBegin);
  const [playbackEndDt, setPlaybackEndDt] = useState(defaultEnd);

  // ── Tag selection ────────────────────────────────────────────────────────────
  const [labelTags, setLabelTags] = useState([]);
  const [objectTags, setObjectTags] = useState(
    selectedObject ? [selectedObject] : [],
  );

  // ── Workflow job selection ───────────────────────────────────────────────────
  // Only relevant when activeFunction === "workflow". Holds one entry from the
  // static WORKFLOW_JOBS list: { jobName, jobNumber }.
  const [selectedJob, setSelectedJob] = useState(null);

  // ── Validation error ─────────────────────────────────────────────────────────
  // Only relevant in full mode (non-playback). Cleared as soon as the user
  // adds at least one label or object tag.
  const [filterError, setFilterError] = useState(null);

  // ── Live API suggestions ─────────────────────────────────────────────────────
  const [labelSuggestions, setLabelSuggestions] = useState([]);
  const [objectSuggestions, setObjectSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const fetchedRef = useRef(false);

  // ── Minimize state ───────────────────────────────────────────────────────────
  const [isMinimized, setIsMinimized] = useState(false);

  // ── Close animation state ────────────────────────────────────────────────────
  const [isClosingInternal, setIsClosingInternal] = useState(false);
  const closeTimerRef = useRef(null);

  // Combined closing flag - either source triggers the CSS exit class
  const isClosing = isClosingInternal || isClosingExternal;

  // ── Derived flags ────────────────────────────────────────────────────────────
  const isPlayback = activeFunction === "playback";
  const isWorkflow = activeFunction === "workflow";
  // Generic label+object full-filter block is for heatmap/spaghetti only -
  // workflow gets its own single-object + jobs-dropdown block below.
  const showFullFilters = mode === "full" && !isPlayback && !isWorkflow;
  // Workflow's object field is needed only on access point #1 (live map
  // iframe, mode="full"). Access points #2/#3 (module/personnel metrics
  // pages) pass mode="dateOnly" and the object is already locked via
  // selectedObject (shown in the locked-object badge below).
  const showWorkflowObjectField = isWorkflow && mode === "full";
  // Workflow's jobs dropdown is needed regardless of mode - the job is never
  // pre-selected by the launching page.
  const showWorkflowJobsField = isWorkflow;
  // Live object suggestions (window.Riot.Api) are needed by the generic
  // full-filter object field, workflow's single-object field, OR the
  // playback object field (full mode only — dateOnly locks via selectedObject).
  const showPlaybackObjectField = isPlayback && mode === "full";
  const needsObjectSuggestions =
    showFullFilters || showWorkflowObjectField || showPlaybackObjectField;

  // ── Clear validation error whenever tags change ───────────────────────────
  useEffect(() => {
    if (labelTags.length > 0 || objectTags.length > 0) {
      setFilterError(null);
    }
  }, [labelTags, objectTags]);

  // ── Sync objectTags when parent changes locked object ───────────────────────
  useEffect(() => {
    if (selectedObject) {
      setObjectTags((prev) =>
        prev.includes(selectedObject) ? prev : [selectedObject, ...prev],
      );
    }
  }, [selectedObject]);

  // ── Reset everything when the active function changes ────────────────────────
  useEffect(() => {
    setBeginDt(defaultBegin());
    setEndDt(defaultEnd());
    setDatetimeDt(defaultBegin());
    setPlaybackEndDt(defaultEnd());
    setLabelTags([]);
    setObjectTags(selectedObject ? [selectedObject] : []);
    setSelectedJob(null);
    setFilterError(null);
    setIsMinimized(false);
    setIsClosingInternal(false);
    fetchedRef.current = false;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, [activeFunction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch live suggestions on mount (full label+object filters, or
  //    workflow's single-object field) ──────────────────────────────────────
  useEffect(() => {
    if (!needsObjectSuggestions || fetchedRef.current) return;
    fetchedRef.current = true;
    setSuggestionsLoading(true);
    Promise.all([fetchLabelNames(), fetchObjectNames()])
      .then(([labels, objects]) => {
        setLabelSuggestions(labels);
        setObjectSuggestions(objects);
      })
      .catch((err) => console.warn("[RtlsFilter] suggestion fetch error:", err))
      .finally(() => setSuggestionsLoading(false));
  }, [needsObjectSuggestions]);

  // ── Cleanup close timer on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // ── Internal animated close ───────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (isClosingInternal || closeTimerRef.current) return;
    setIsClosingInternal(true);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      onClose?.();
    }, CLOSE_ANIM_MS);
  }, [isClosingInternal, onClose]);

  // ── Minimize toggle ────────────────────────────────────────────────────────────
  const handleMinimizeToggle = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // ── ESC key handler ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Escape") return;
      if (isMinimized) {
        setIsMinimized(false);
      } else {
        handleClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isMinimized, handleClose]);

  // ── Apply ─────────────────────────────────────────────────────────────────────
  const handleApply = useCallback(() => {
    if (!onApply) return;

    if (activeFunction === "playback") {
      // In full mode the user must pick an object — there is no pre-locked
      // selectedObject from the launching page.
      // In dateOnly mode selectedObject is already locked, so no check needed.
      if (mode === "full" && objectTags.length === 0) {
        setFilterError("Please select an Object Name before applying.");
        return;
      }

      // Validate both timestamps are present and ordered correctly
      const beginTs = fromDatetimeLocal(datetimeDt);
      const endTs = fromDatetimeLocal(playbackEndDt);

      if (!beginTs || !endTs) {
        setFilterError("Both a start and end time are required.");
        return;
      }
      if (beginTs >= endTs) {
        setFilterError("Playback start must be earlier than end time.");
        return;
      }

      onApply({
        datetimeTs: beginTs,
        endDatetimeTs: endTs,
        // Pass the selected object so rtlsMap.js can forward it to usePlaybackMap.
        // In full mode this comes from the object picker below;
        // in dateOnly mode it comes from the locked selectedObject prop.
        ...(mode === "full"
          ? { objectNames: objectTags }
          : selectedObject
            ? { objectNames: [selectedObject] }
            : {}),
      });

      setIsMinimized(true);
      return;
    }

    // ── Workflow mode: requires exactly one object AND one job ───────────────
    // dateOnly mode gets its object from the locked selectedObject prop;
    // full mode gets it from objectTags[0] (TagSelect is capped at maxTags=1).
    if (isWorkflow) {
      const workflowObjectName =
        mode === "dateOnly" ? selectedObject : (objectTags[0] ?? null);

      if (!workflowObjectName || !selectedJob) {
        setFilterError(
          !workflowObjectName
            ? "Please select an object before applying."
            : "Please select a job before applying.",
        );
        return; // do NOT apply or minimize
      }

      onApply({
        beginTs: fromDatetimeLocal(beginDt),
        endTs: fromDatetimeLocal(endDt),
        // Pass the raw datetime-local strings alongside Unix timestamps.
        // dataServe.js uses these for direct SQL DATETIME comparisons so the
        // filter respects the exact hour/minute the user selected — not just
        // the date portion that Unix-second rounding can collapse to.
        // Format: "YYYY-MM-DDTHH:MM" (SQL Server accepts ISO-8601 T-notation).
        beginDateTime: beginDt,
        endDateTime: endDt,
        objectName: workflowObjectName,
        jobName: selectedJob.jobName,
        jobNumber: selectedJob.jobNumber,
      });

      setIsMinimized(true);
      return;
    }

    // ── Full mode: require at least one label OR one object name ─────────────
    // dateOnly mode is always pre-locked to selectedObject so no check needed.
    if (showFullFilters && labelTags.length === 0 && objectTags.length === 0) {
      setFilterError(
        "Please select at least one Object Name or Label before applying.",
      );
      return; // do NOT apply or minimize
    }

    const filters = {
      beginTs: fromDatetimeLocal(beginDt),
      endTs: fromDatetimeLocal(endDt),
    };

    if (mode === "full") {
      if (labelTags.length > 0) filters.labels = labelTags;
      if (objectTags.length > 0) filters.objectNames = objectTags;
    }

    if (mode === "dateOnly" && selectedObject) {
      // Wrap the single locked object in an array under the correct key.
      filters.objectNames = [selectedObject];
    }

    onApply(filters);

    // Auto-minimize after applying so the map view is unobstructed
    setIsMinimized(true);
  }, [
    activeFunction,
    mode,
    showFullFilters,
    isWorkflow,
    beginDt,
    endDt,
    datetimeDt,
    playbackEndDt,
    labelTags,
    objectTags,
    selectedObject,
    selectedJob,
    onApply,
  ]);

  // ── Summary text ──────────────────────────────────────────────────────────────
  const summary = buildSummary({
    activeFunction,
    beginDt,
    endDt,
    datetimeDt,
    playbackEndDt,
    labelTags,
    objectTags,
    selectedObject,
    selectedJob,
    mode,
  });

  // ── Panel class ───────────────────────────────────────────────────────────────
  const panelClass = [
    "rtlsf-panel",
    isExpanded ? "rtlsf-panel--expanded" : "",
    isClosing ? "rtlsf-panel--closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      className={panelClass}
      role="dialog"
      aria-label={FUNCTION_TITLES[activeFunction] ?? "Map Function Options"}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="rtlsf-header">
        <div className="rtlsf-header-left">
          <span className="rtlsf-header-icon">
            <FunctionIcon fn={activeFunction} />
          </span>
          <span className="rtlsf-header-title">
            {FUNCTION_TITLES[activeFunction] ?? "Options"}
          </span>
        </div>

        <div className="rtlsf-header-actions">
          <button
            className="rtlsf-minimize-btn"
            onClick={handleMinimizeToggle}
            aria-label={
              isMinimized ? "Expand filter panel" : "Minimize filter panel"
            }
            title={isMinimized ? "Expand" : "Minimize"}
          >
            <ChevronIcon up={isMinimized} />
          </button>
          <button
            className="rtlsf-close-btn"
            onClick={handleClose}
            aria-label="Close filter panel"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Minimized summary bar ──────────────────────────────────────────────
          Shown only when isMinimized=true. Clicking expands the panel. */}
      {isMinimized && (
        <button
          className="rtlsf-summary"
          onClick={handleMinimizeToggle}
          aria-label="Expand filter panel"
          title="Click to expand"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="rtlsf-summary-text">{summary}</span>
        </button>
      )}

      {/* ── Collapsible region ────────────────────────────────────────────────
          Wraps: locked-object badge + body + footer.
          CSS max-height transition animates open ↔ collapsed. */}
      <div
        className={`rtlsf-collapsible${
          isMinimized ? " rtlsf-collapsible--collapsed" : ""
        }`}
      >
        {/* ── Locked object badge (dateOnly mode) ──────────────────────── */}
        {selectedObject && (
          <div className="rtlsf-locked-object">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="rtlsf-locked-label">Filtered to:</span>
            <span className="rtlsf-locked-value">{selectedObject}</span>
          </div>
        )}

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="rtlsf-body">
          {/* PLAYBACK: start + end datetime pickers */}
          {isPlayback && (
            <>
              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-datetime">
                  Playback Start
                </label>
                <input
                  id="rtlsf-datetime"
                  className="rtlsf-input"
                  type="datetime-local"
                  value={datetimeDt}
                  max={playbackEndDt}
                  onChange={(e) => setDatetimeDt(e.target.value)}
                />
              </div>

              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-datetime-end">
                  Playback End
                </label>
                <input
                  id="rtlsf-datetime-end"
                  className="rtlsf-input"
                  type="datetime-local"
                  value={playbackEndDt}
                  min={datetimeDt}
                  onChange={(e) => setPlaybackEndDt(e.target.value)}
                />
              </div>
            </>
          )}
          {/* PLAYBACK: object name picker (full mode only — dateOnly locks via selectedObject) */}
          {isPlayback && showPlaybackObjectField && (
            <>
              <div className="rtlsf-field">
                <label
                  className="rtlsf-label"
                  htmlFor="rtlsf-playback-object-input"
                >
                  Object Name
                </label>
                <TagSelect
                  id="rtlsf-playback-object-input"
                  placeholder="Type to search objects…"
                  suggestions={objectSuggestions}
                  suggestionsLoading={suggestionsLoading}
                  tags={objectTags}
                  onChange={setObjectTags}
                  maxTags={1}
                />
              </div>
              <div className="rtlsf-divider" />
            </>
          )}

          {/* NON-PLAYBACK: date range */}
          {!isPlayback && (
            <>
              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-begin">
                  From
                </label>
                <input
                  id="rtlsf-begin"
                  className="rtlsf-input"
                  type="datetime-local"
                  value={beginDt}
                  max={endDt}
                  onChange={(e) => setBeginDt(e.target.value)}
                />
              </div>
              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-end">
                  To
                </label>
                <input
                  id="rtlsf-end"
                  className="rtlsf-input"
                  type="datetime-local"
                  value={endDt}
                  min={beginDt}
                  onChange={(e) => setEndDt(e.target.value)}
                />
              </div>
            </>
          )}

          {/* WORKFLOW: single object name (full mode only) + jobs dropdown
              (always - never pre-filled by the launching page) */}
          {showWorkflowJobsField && (
            <>
              <div className="rtlsf-divider" />
              {showWorkflowObjectField && (
                <div className="rtlsf-field">
                  <label
                    className="rtlsf-label"
                    htmlFor="rtlsf-workflow-object-input"
                  >
                    Object Name
                  </label>
                  <TagSelect
                    id="rtlsf-workflow-object-input"
                    placeholder="Type to search objects…"
                    suggestions={objectSuggestions}
                    suggestionsLoading={suggestionsLoading}
                    tags={objectTags}
                    onChange={setObjectTags}
                    maxTags={1}
                  />
                </div>
              )}
              <div className="rtlsf-field">
                <label
                  className="rtlsf-label"
                  htmlFor="rtlsf-workflow-job-input"
                >
                  Job
                </label>
                <JobSelect
                  id="rtlsf-workflow-job-input"
                  placeholder="Type to search jobs…"
                  jobs={WORKFLOW_JOBS}
                  value={selectedJob}
                  onChange={setSelectedJob}
                />
              </div>
            </>
          )}

          {/* FULL MODE: label + object name with live TagSelect */}
          {showFullFilters && (
            <>
              <div className="rtlsf-divider" />
              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-label-input">
                  Label
                </label>
                <TagSelect
                  id="rtlsf-label-input"
                  placeholder="Type to search labels…"
                  suggestions={labelSuggestions}
                  suggestionsLoading={suggestionsLoading}
                  tags={labelTags}
                  onChange={setLabelTags}
                />
              </div>
              <div className="rtlsf-field">
                <label className="rtlsf-label" htmlFor="rtlsf-object-input">
                  Object Name
                </label>
                <TagSelect
                  id="rtlsf-object-input"
                  placeholder="Type to search objects…"
                  suggestions={objectSuggestions}
                  suggestionsLoading={suggestionsLoading}
                  tags={objectTags}
                  onChange={setObjectTags}
                />
              </div>
            </>
          )}

          {/* ── Validation error ──────────────────────────────────────────
              Shown in full mode when the user hits Apply with no label
              and no object selected. Clears automatically when they add
              at least one tag to either field. */}
          {filterError && (
            <div className="rtlsf-error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              <span>{filterError}</span>
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="rtlsf-footer">
          <button className="rtlsf-btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button className="rtlsf-btn-primary" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default RtlsFilter;
